import React, { useMemo, useState, useEffect } from 'react'
import SimpleAccordion from '../../../ui_reusable/SimpleAccordion'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  FormGrid,
  InputsWrapper,
  ParameterLabel,
  ParametersInfo,
  ParameterValue,
  RowTitle,
  StyledInput,
} from './styles/ParametersForm.styles'
import {
  ErrorMessage,
  Label,
  LabelFaded,
  RequiredLabelStar,
} from '../../../ui_reusable/styles/CreateInputUtils.styles'
import {
  FormSelectPicker,
  FormMultiSelectPicker,
} from '../../../ui_reusable/StandardSelectPickers'
import { StandardInput } from '../../../ui_reusable/StandardInputs'
import OptimizeTimeframeSelector from './OptimizeTimeframeSelector'
import {
  OptimizeArtifactRequest,
  OptimizeConfigRequestBudgetType,
  OptimizeConfigRequestModelDataFrequency,
  ProjectSolutionDriver,
} from '@/api/generated/models'
import { useModelParameters } from './hooks/useModelParameters'
import ModelNameEdit from './shared/ModelNameEdit'
import StandardComboBox, {
  SearchableItem,
} from '../../../ui_reusable/StandardComboBox'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import CustomIcons from '../../../ui_reusable/CustomIcons'
import theme from '../../../../styles/theme'
import { Message, Tooltip, Whisper } from 'rsuite'

type RegionOptionSource = {
  region?: { id?: number; name?: string } | null
}

type OptimizeParametersFormProps = {
  projectSolutionDrivers: ProjectSolutionDriver[]
  /** Regional variants for the selected outcome — fetched once in BuildModel. */
  regionalDatasets?: RegionOptionSource[]
}

const OptimizeParametersForm = ({
  projectSolutionDrivers,
  regionalDatasets = [],
}: OptimizeParametersFormProps) => {
  const { pushToast } = useStandardToaster()
  const {
    register,
    unregister,
    watch,
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useFormContext<OptimizeArtifactRequest>()

  const { handleDependentDriverChange, dateRange } = useModelParameters(
    projectSolutionDrivers,
    false,
    'timeframe',
  )

  const dependentDriverId = useWatch({
    control,
    name: 'dependent_driver_id',
  })

  const modelDataFrequency = useWatch({
    control,
    name: 'optimize_config.model_data_frequency',
  })

  const budgetType = useWatch({
    control,
    name: 'optimize_config.budget_type',
  })

  const dependentDriverIsRevenue = useWatch({
    control,
    name: 'optimize_config.dependent_driver_is_revenue',
  })

  const roiCalculationPeriod = useWatch({
    control,
    name: 'optimize_config.roi_calculation_period',
  })

  const revenuePerKpiId = useWatch({
    control,
    name: 'optimize_config.revenue_per_kpi_id',
  })

  const selectedRegionIds = useWatch({
    control,
    name: 'selected_region_ids' as any,
  })

  const dependentDriver = useMemo(() => {
    if (!dependentDriverId) return null
    const projectSolutionDriver = projectSolutionDrivers?.find(
      (psd) => psd.driver?.id === dependentDriverId,
    )
    return projectSolutionDriver?.driver ?? null
  }, [dependentDriverId, projectSolutionDrivers])

  const revenuePerKpiDrivers = useMemo(() => {
    return projectSolutionDrivers?.filter(
      (psd) => psd.driver?.category === 'revenue_per_kpi',
    )
  }, [projectSolutionDrivers])

  const geographicalOptions = useMemo(() => {
    const options: { label: string; value: string | number }[] = [
      { label: 'National', value: 'national' },
    ]

    regionalDatasets.forEach((regionDataset) => {
      if (regionDataset.region?.name && regionDataset.region?.id) {
        options.push({
          label: regionDataset.region.name,
          value: regionDataset.region.id,
        })
      }
    })

    return options
  }, [regionalDatasets])

  const searchableDrivers: SearchableItem[] = useMemo(() => {
    return (
      projectSolutionDrivers?.map((projectSolutionDriver) => ({
        id: String(projectSolutionDriver.driver?.id ?? ''),
        name: projectSolutionDriver.driver?.name ?? '',
      })) ?? []
    )
  }, [projectSolutionDrivers])

  const [selectedDriverItems, setSelectedDriverItems] = useState<
    SearchableItem[]
  >([])

  useEffect(() => {
    if (dependentDriverId) {
      const matchingDriver = projectSolutionDrivers?.find(
        (psd) => psd.driver?.id === dependentDriverId,
      )
      if (matchingDriver) {
        setSelectedDriverItems([
          {
            id: String(matchingDriver.driver?.id ?? ''),
            name: matchingDriver.driver?.name ?? '',
          },
        ])
      }
    } else {
      setSelectedDriverItems([])
    }
  }, [dependentDriverId, projectSolutionDrivers])

  // Helper to determine max_lag defaults/limits based on model data frequency
  const getMaxLagConfig = (
    freq?: OptimizeConfigRequestModelDataFrequency | string | null,
  ) => {
    switch (freq) {
      case 'daily':
        return { defaultValue: 60, max: 90 }
      case 'weekly':
        return { defaultValue: 8, max: 12 }
      case 'monthly':
      default:
        return { defaultValue: 2, max: 4 }
    }
  }

  // Ensure max_lag is registered with dynamic validation based on modelDataFrequency.
  useEffect(() => {
    const { max } = getMaxLagConfig(modelDataFrequency)

    register('optimize_config.budget_type', {
      required: 'Budget type is required',
    })
    register('optimize_config.spend_constraint', {
      required: 'Spend constraint is required',
      valueAsNumber: true,
      min: { value: 0, message: 'Spend constraint must be between 0 and 100' },
      max: {
        value: 100,
        message: 'Spend constraint must be between 0 and 100',
      },
    })
    register('optimize_config.max_lag', {
      required: 'Max lag is required',
      valueAsNumber: true,
      min: { value: 1, message: 'Max lag must be at least 1' },
      // dynamic upper bound via validate so the message can be customized
      validate: (value: any) => {
        const numeric = Number(value)
        if (value == null || value === '') return 'Max lag is required'
        if (isNaN(numeric)) return 'Max lag must be a number'
        if (numeric < 1) return 'Max lag must be at least 1'
        if (numeric > max)
          return `Max lag must be at most ${max} for the selected granularity`
        return true
      },
    })
    register('optimize_config.roi_impression', {
      validate: (value: any) => {
        const isRevenue = getValues(
          'optimize_config.dependent_driver_is_revenue',
        )
        const revPerKpi = getValues(
          'optimize_config.revenue_per_kpi_id',
        )
        if (isRevenue === false && revPerKpi == null) return true
        if (!value || value === '') return 'ROI impressions is required'
        return true
      },
    })
    register('optimize_config.roi_reach', {
      validate: (value: any) => {
        const isRevenue = getValues(
          'optimize_config.dependent_driver_is_revenue',
        )
        const revPerKpi = getValues(
          'optimize_config.revenue_per_kpi_id',
        )
        if (isRevenue === false && revPerKpi == null) return true
        if (!value || value === '') return 'ROI reach is required'
        return true
      },
    })
    register('optimize_config.expected_media_contribution', {
      valueAsNumber: true,
      min: {
        value: 0,
        message: 'Expected media contribution must be between 0 and 100',
      },
      max: {
        value: 100,
        message: 'Expected media contribution must be between 0 and 100',
      },
    })
    register('optimize_config.model_data_frequency', {
      required: 'Model data frequency is required',
    })
    register('optimize_config.timeframe' as any, {
      validate: (value: any) => {
        if (!value?.from || !value?.to) {
          return 'Optimization timeframe is required'
        }
        return true
      },
    })
    register('optimize_config.roi_calculation_period')

    return () => {
      unregister('optimize_config.dependent_driver_is_revenue', {
        keepValue: true,
      })
      unregister('optimize_config.budget_type', { keepValue: true })
      unregister('optimize_config.spend_constraint', { keepValue: true })
      unregister('optimize_config.max_lag', { keepValue: true })
      unregister('optimize_config.roi_impression', { keepValue: true })
      unregister('optimize_config.roi_reach', { keepValue: true })
      unregister('optimize_config.expected_media_contribution', {
        keepValue: true,
      })
      unregister('optimize_config.model_data_frequency', { keepValue: true })
      unregister('optimize_config.timeframe' as any, { keepValue: true })
      unregister('optimize_config.roi_calculation_period', { keepValue: true })
    }
    // Re-run registration when modelDataFrequency changes so validation stays in sync
  }, [register, unregister, modelDataFrequency])

  useEffect(() => {
    if (!roiCalculationPeriod) {
      setValue('optimize_config.roi_calculation_period', 'optimize_period', {
        shouldValidate: false,
      })
    }
  }, [roiCalculationPeriod, setValue])

  // Helper to update max_lag when user changes frequency (called from onChange, not useEffect)
  const updateMaxLagForFrequency = (
    newFrequency: OptimizeConfigRequestModelDataFrequency,
  ) => {
    const { defaultValue } = getMaxLagConfig(newFrequency)
    // Don't validate immediately - the registration useEffect needs to run first
    // to update the validation rules for the new frequency
    setValue('optimize_config.max_lag', defaultValue, { shouldValidate: false })
    pushToast(
      <Message type="info" showIcon>
        Max lag adjusted to {defaultValue} for {newFrequency} frequency
      </Message>,
      { placement: 'topCenter', duration: 4000 },
    )
  }

  // Clear budget-related fields when budget_type changes
  useEffect(() => {
    if (budgetType === 'fixed') {
      // Clear flex fields
      setValue('optimize_config.target_roi', null)
      setValue('optimize_config.target_mroi', null)
    } else if (budgetType === 'flex_target_roi') {
      // Clear fixed and marginal ROI fields
      setValue('optimize_config.budget_amount', null)
      setValue('optimize_config.target_mroi', null)
    } else if (budgetType === 'flex_target_mroi') {
      // Clear fixed and target ROI fields
      setValue('optimize_config.budget_amount', null)
      setValue('optimize_config.target_roi', null)
    }
  }, [budgetType, setValue])

  // Clear revenue_per_kpi_id when outcome is set to revenue
  // (revenue_per_kpi is only needed when outcome is non-revenue)
  useEffect(() => {
    if (dependentDriverIsRevenue === true) {
      setValue('optimize_config.revenue_per_kpi_id', null)
    }
  }, [dependentDriverIsRevenue, setValue])

  useEffect(() => {
    if (dependentDriverIsRevenue === true || revenuePerKpiId != null) {
      setValue('optimize_config.expected_media_contribution', null)
    } else if (watch('optimize_config.expected_media_contribution') == null) {
      setValue('optimize_config.expected_media_contribution', 0.4)
    }
  }, [dependentDriverIsRevenue, revenuePerKpiId, setValue])

  // ROI fields: Medium default when visible; null for non-revenue without revenue-per-KPI
  useEffect(() => {
    const showRoi =
      dependentDriverIsRevenue !== false || revenuePerKpiId != null
    if (!showRoi) {
      setValue('optimize_config.roi_impression', null)
      setValue('optimize_config.roi_reach', null)
      return
    }
    if (!getValues('optimize_config.roi_impression')) {
      setValue('optimize_config.roi_impression', 'standard')
    }
    if (!getValues('optimize_config.roi_reach')) {
      setValue('optimize_config.roi_reach', 'standard')
    }
  }, [dependentDriverIsRevenue, revenuePerKpiId, getValues, setValue])

  const handleDriverComboChange = (items: SearchableItem[]) => {
    setSelectedDriverItems(items)
    if (items && items.length > 0) {
      const selectedId = Number(items[0].id)
      const selectedDriver = projectSolutionDrivers?.find(
        (psd) => psd.driver?.id === selectedId,
      )
      if (selectedDriver?.driver?.id) {
        handleDependentDriverChange(selectedDriver.driver.id)

        // Set model_data_frequency based on the selected outcome variable's interval
        if (selectedDriver.driver.interval) {
          const newFrequency = selectedDriver.driver
            .interval as OptimizeConfigRequestModelDataFrequency
          setValue('optimize_config.model_data_frequency', newFrequency, {
            shouldValidate: true,
          })
          // Also update max_lag to appropriate default for the new frequency
          updateMaxLagForFrequency(newFrequency)
        }
      }
    } else {
      handleDependentDriverChange(null)
    }

    // Don't reset region selection here - let BuildModel.page.tsx handle validation
    // after fetching regional datasets for the new outcome variable.
    // If the new outcome variable doesn't support the current regions,
    // the reconciliation logic in BuildModel.page.tsx will clear them and notify the user.
  }

  const handleGeographicalGranularityChange = (
    selectedValues: (string | number)[],
  ) => {
    if (selectedValues.length === 0) {
      // Nothing selected, default to National
      setValue('selected_region_ids' as any, ['national'])
      setValue('is_national_selected' as any, true)
      setValue('optimize_config.geo_granularity', 'national', {
        shouldValidate: true,
      })
      return
    }

    const hasNational = selectedValues.includes('national')
    const regionIds = selectedValues.filter((v) => v !== 'national')

    if (hasNational && regionIds.length > 0) {
      // Both National and regions selected - keep only the most recent selection
      // If National was just added, clear regions; if region was just added, clear National
      const previousValues = (selectedRegionIds as (string | number)[]) || []
      const previousHadNational = previousValues.includes('national')

      if (!previousHadNational && hasNational) {
        // National was just added, so clear regions
        setValue('selected_region_ids' as any, ['national'])
        setValue('is_national_selected' as any, true)
        setValue('optimize_config.geo_granularity', 'national', {
          shouldValidate: true,
        })
      } else {
        // Region was just added, so clear National
        setValue('selected_region_ids' as any, regionIds)
        setValue('is_national_selected' as any, false)
        setValue('optimize_config.geo_granularity', 'regional', {
          shouldValidate: true,
        })
      }
    } else if (hasNational) {
      // Only National selected
      setValue('selected_region_ids' as any, ['national'])
      setValue('is_national_selected' as any, true)
      setValue('optimize_config.geo_granularity', 'national', {
        shouldValidate: true,
      })
    } else {
      // Only regions selected
      setValue('selected_region_ids' as any, regionIds)
      setValue('is_national_selected' as any, false)
      setValue('optimize_config.geo_granularity', 'regional', {
        shouldValidate: true,
      })
    }
  }

  const { max: currentMaxLagMax } = getMaxLagConfig(modelDataFrequency)

  return (
    <SimpleAccordion.Panel header="Set Parameters" eventKey={'2'}>
      <FormGrid>
        <ModelNameEdit />
        <RowTitle>Outcome:</RowTitle>
        <InputsWrapper>
          <StyledInput>
            <Label htmlFor="dependent_driver">Set outcome variable</Label>
            <StandardComboBox
              data={searchableDrivers}
              onChange={handleDriverComboChange}
              placeholder="Select outcome variable..."
              maxSelected={1}
              alwaysShowList={true}
              valueList={selectedDriverItems}
              singleSelection={true}
              style={{ width: '20rem' }}
              allowCreate={false}
            />
            <ErrorMessage>
              {errors.dependent_driver_id?.message || ''}
            </ErrorMessage>
          </StyledInput>
          <StyledInput>
            <LabelFaded htmlFor="outcome_is_revenue">
              Outcome is revenue?
              <RequiredLabelStar>*</RequiredLabelStar>
            </LabelFaded>
            <FormSelectPicker
              id="outcome_is_revenue"
              data={[
                { label: 'Revenue', value: 'revenue' },
                { label: 'Non-revenue', value: 'non_revenue' },
              ]}
              value={
                watch('optimize_config.dependent_driver_is_revenue')
                  ? 'revenue'
                  : 'non_revenue'
              }
              onChange={(value) =>
                setValue(
                  'optimize_config.dependent_driver_is_revenue',
                  value === 'revenue' ? true : false,
                  { shouldValidate: true },
                )
              }
            />
            <ErrorMessage>
              {errors.optimize_config?.dependent_driver_is_revenue?.message ||
                ''}
            </ErrorMessage>
          </StyledInput>
          {watch('optimize_config.dependent_driver_is_revenue') === false && (
            <StyledInput>
              <LabelFaded htmlFor="revenue_per_kpi">
                Set revenue per KPI
                <Whisper
                  placement="top"
                  trigger="hover"
                  speaker={
                    <Tooltip>
                      Revenue per KPI is optional when outcome = Non-revenue
                    </Tooltip>
                  }
                >
                  <span
                    style={{
                      marginLeft: '0.3rem',
                      verticalAlign: 'middle',
                      cursor: 'help',
                      display: 'inline-block',
                    }}
                  >
                    <CustomIcons.QuestionIcon
                      color={theme.colors.veryVeryFadedText}
                      size="xxs"
                    />
                  </span>
                </Whisper>
              </LabelFaded>
              <FormSelectPicker
                id="revenue_per_kpi"
                data={
                  revenuePerKpiDrivers?.map((psd) => ({
                    label: psd.driver?.name ?? '',
                    value: String(psd.driver?.id ?? ''),
                  })) ?? []
                }
                value={String(
                  watch('optimize_config.revenue_per_kpi_id') ?? '',
                )}
                onChange={(value) =>
                  setValue(
                    'optimize_config.revenue_per_kpi_id',
                    value ? Number(value) : null,
                  )
                }
              />
              <ErrorMessage>
                {errors.optimize_config?.revenue_per_kpi_id?.message || ''}
              </ErrorMessage>
            </StyledInput>
          )}
        </InputsWrapper>
        <RowTitle>Granularity:</RowTitle>
        <InputsWrapper>
          <StyledInput>
            <LabelFaded htmlFor="model_data_granularity">
              Model data granularity
              <RequiredLabelStar>*</RequiredLabelStar>
            </LabelFaded>
            <FormSelectPicker
              id="model_data_granularity"
              data={[
                { label: 'Monthly', value: 'monthly' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Daily', value: 'daily' },
              ]}
              value={watch('optimize_config.model_data_frequency')}
              onChange={(value) => {
                const newFrequency =
                  value as OptimizeConfigRequestModelDataFrequency
                setValue('optimize_config.model_data_frequency', newFrequency, {
                  shouldValidate: true,
                })
                // Update max_lag to appropriate default when user changes frequency
                updateMaxLagForFrequency(newFrequency)
              }}
            />
            <ErrorMessage>
              {errors.optimize_config?.model_data_frequency?.message || ''}
            </ErrorMessage>
          </StyledInput>
          <StyledInput>
            <LabelFaded htmlFor="geographical_granularity">
              Geographical granularity
            </LabelFaded>
            <FormMultiSelectPicker
              id="geographical_granularity"
              data={geographicalOptions}
              value={selectedRegionIds || ['national']}
              onChange={handleGeographicalGranularityChange}
              placeholder="Select National or regions..."
            />
            <ErrorMessage>
              {errors.optimize_config?.geo_granularity?.message || ''}
            </ErrorMessage>
          </StyledInput>
        </InputsWrapper>
        <OptimizeTimeframeSelector
          training_timeframe={watch('timeframe' as any)}
          optimize_timeframe={
            watch('optimize_config.timeframe' as any) ?? { from: '', to: '' }
          }
          onTrainingTimeframeChange={(value) =>
            setValue('timeframe' as any, value, {
              shouldValidate: true,
            })
          }
          onOptimizeTimeframeChange={(value) =>
            setValue('optimize_config.timeframe' as any, value, {
              shouldValidate: true,
            })
          }
          dependentDriver={dependentDriver}
          modelDataFrequency={modelDataFrequency ?? 'weekly'}
          roiCalculationPeriod={roiCalculationPeriod ?? 'optimize_period'}
          onRoiCalculationPeriodChange={(value) =>
            setValue('optimize_config.roi_calculation_period', value, {
              shouldValidate: true,
            })
          }
          error={{
            modeling: (errors as any).timeframe,
            optimize: (errors as any).optimize_timeframe,
          }}
        />
        <RowTitle>Optimization:</RowTitle>
        <InputsWrapper>
          <StyledInput>
            <LabelFaded htmlFor="total_budget">
              Total budget
              <RequiredLabelStar>*</RequiredLabelStar>
            </LabelFaded>
            <FormSelectPicker
              id="total_budget"
              data={[
                { label: 'Auto', value: 'auto' },
                { label: 'Fixed', value: 'fixed' },
                {
                  label: 'Flexible with target ROI',
                  value: 'flex_target_roi',
                },
                {
                  label: 'Flexible with marginal ROI',
                  value: 'flex_target_mroi',
                },
              ]}
              value={watch('optimize_config.budget_type')}
              onChange={(value) =>
                setValue(
                  'optimize_config.budget_type',
                  value as OptimizeConfigRequestBudgetType,
                  { shouldValidate: true },
                )
              }
            />
            <ErrorMessage>
              {errors.optimize_config?.budget_type?.message || ''}
            </ErrorMessage>
          </StyledInput>
          {watch('optimize_config.budget_type') === 'fixed' && (
            <StyledInput>
              <LabelFaded htmlFor="fixed_budget">
                Fixed budget
                <RequiredLabelStar>*</RequiredLabelStar>
              </LabelFaded>
              <StandardInput
                id="fixed_budget"
                type="number"
                {...register('optimize_config.budget_amount', {
                  valueAsNumber: true,
                })}
              />
              <ErrorMessage>
                {errors.optimize_config?.budget_amount?.message || ''}
              </ErrorMessage>
            </StyledInput>
          )}
          {watch('optimize_config.budget_type') === 'flex_target_roi' && (
            <StyledInput>
              <LabelFaded htmlFor="target_roi">Target ROI</LabelFaded>
              <StandardInput
                id="target_roi"
                type="number"
                {...register('optimize_config.target_roi', {
                  valueAsNumber: true,
                })}
              />
              <ErrorMessage>
                {errors.optimize_config?.target_roi?.message || ''}
              </ErrorMessage>
            </StyledInput>
          )}
          {watch('optimize_config.budget_type') === 'flex_target_mroi' && (
            <StyledInput>
              <LabelFaded htmlFor="margina_roi">Marginal ROI</LabelFaded>
              <StandardInput
                id="marginal_roi"
                type="number"
                {...register('optimize_config.target_mroi', {
                  valueAsNumber: true,
                })}
              />
              <ErrorMessage>
                {errors.optimize_config?.target_mroi?.message || ''}
              </ErrorMessage>
            </StyledInput>
          )}
          <StyledInput>
            <LabelFaded htmlFor="channel_constraints">
              Channel constraints (%)
              <RequiredLabelStar>*</RequiredLabelStar>
            </LabelFaded>
            <StandardInput
              id="channel_constraints"
              type="number"
              value={String(
                watch('optimize_config.spend_constraint') * 100 || '',
              )}
              onChange={(e) => {
                const percentageValue = Number(e.target.value)
                if (!isNaN(percentageValue)) {
                  setValue(
                    'optimize_config.spend_constraint',
                    percentageValue / 100,
                    { shouldValidate: true },
                  )
                }
              }}
            />
            <ErrorMessage>
              {errors.optimize_config?.spend_constraint?.message || ''}
            </ErrorMessage>
          </StyledInput>
        </InputsWrapper>
        <RowTitle>Assumptions:</RowTitle>
        <InputsWrapper>
          <StyledInput>
            <LabelFaded htmlFor="max_lag_length">
              Max lag length (1-{currentMaxLagMax}){' '}
              <RequiredLabelStar>*</RequiredLabelStar>
            </LabelFaded>
            <StandardInput
              id="max_lag_length"
              type="number"
              min={1}
              max={currentMaxLagMax}
              {...register('optimize_config.max_lag')}
            />
            <ErrorMessage>
              {errors.optimize_config?.max_lag?.message || ''}
            </ErrorMessage>
          </StyledInput>
          <StyledInput>
            <LabelFaded htmlFor="response_effects">Response effects</LabelFaded>
            <FormSelectPicker
              id="response_effects"
              data={[
                { label: 'Adstock→Hill', value: 'adstock-hill' },
                { label: 'Hill→Adstock', value: 'hill-adstock' },
              ]}
              value={
                watch('optimize_config.hill_before_adstock') === true
                  ? 'hill-adstock'
                  : 'adstock-hill'
              }
              onChange={(value) =>
                setValue(
                  'optimize_config.hill_before_adstock',
                  value === 'adstock-hill' ? false : true,
                )
              }
            />
            <ErrorMessage>
              {errors.optimize_config?.hill_before_adstock?.message || ''}
            </ErrorMessage>
          </StyledInput>
          {(dependentDriverIsRevenue !== false || revenuePerKpiId != null) && (
            <>
              <StyledInput>
                <LabelFaded htmlFor="roi_impressions">
                  ROI impressions
                  <RequiredLabelStar>*</RequiredLabelStar>
                </LabelFaded>
                <FormSelectPicker
                  id="roi_impressions"
                  data={[
                    { label: 'Medium', value: 'standard' },
                    { label: 'Low', value: 'conservative' },
                    { label: 'Full', value: 'aggressive' },
                    {
                      label: 'Channel level values',
                      value: 'channel_level_values',
                    },
                  ]}
                  value={watch('optimize_config.roi_impression')}
                  onChange={(value) =>
                    setValue(
                      'optimize_config.roi_impression',
                      String(value || ''),
                      {
                        shouldValidate: true,
                      },
                    )
                  }
                />
                <ErrorMessage>
                  {errors.optimize_config?.roi_impression?.message || ''}
                </ErrorMessage>
              </StyledInput>
              <StyledInput>
                <LabelFaded htmlFor="roi_reach">
                  ROI reach/frequency
                  <RequiredLabelStar>*</RequiredLabelStar>
                </LabelFaded>
                <FormSelectPicker
                  id="roi_reach"
                  data={[
                    { label: 'Medium', value: 'standard' },
                    { label: 'Low', value: 'conservative' },
                    { label: 'Full', value: 'aggressive' },
                    {
                      label: 'Channel level values',
                      value: 'channel_level_values',
                    },
                  ]}
                  value={watch('optimize_config.roi_reach')}
                  onChange={(value) =>
                    setValue('optimize_config.roi_reach', String(value || ''), {
                      shouldValidate: true,
                    })
                  }
                />
                <ErrorMessage>
                  {errors.optimize_config?.roi_reach?.message || ''}
                </ErrorMessage>
              </StyledInput>
            </>
          )}
          {dependentDriverIsRevenue === false && revenuePerKpiId == null && (
            <StyledInput>
              <LabelFaded htmlFor="expected_media_contribution">
                Expected Media Contribution (%)
              </LabelFaded>
              <StandardInput
                id="expected_media_contribution"
                type="number"
                value={String(
                  (watch('optimize_config.expected_media_contribution') ?? 0) *
                    100 || '',
                )}
                onChange={(e) => {
                  const percentageValue = Number(e.target.value)
                  if (!isNaN(percentageValue)) {
                    setValue(
                      'optimize_config.expected_media_contribution',
                      percentageValue / 100,
                      { shouldValidate: true },
                    )
                  }
                }}
              />
              <ErrorMessage>
                {errors.optimize_config?.expected_media_contribution?.message ||
                  ''}
              </ErrorMessage>
            </StyledInput>
          )}
        </InputsWrapper>
      </FormGrid>
      <ParametersInfo>
        <ParameterLabel>
          Observations in timeframe:{' '}
          <ParameterValue>
            {dependentDriver?.total_observations || 0}
          </ParameterValue>
        </ParameterLabel>
        <ParameterLabel>
          Model data granularity:{' '}
          <ParameterValue>
            {watch('optimize_config.model_data_frequency') || 'N/A'}
          </ParameterValue>
        </ParameterLabel>
        <ParameterLabel>
          Marginal ROI: <ParameterValue>LogNormal</ParameterValue>
        </ParameterLabel>
      </ParametersInfo>
    </SimpleAccordion.Panel>
  )
}

export default OptimizeParametersForm
